import React, { useState } from 'react';
import {
  Box,
  Container,
  Divider,
  Grid,
  Stack,
  useTheme,
} from '@mui/material';
import { ViewProps } from './types';
import ProfileCard from './cards/profile-card';
import PartnerEditor from './editors/edit-partner-form';
import BillingInfoEditor from './editors/edit-billing-info-form';
import PartnerDetailEditor from './editors/edit-partner-detail-form';
import ProfileDetailCard from './cards/profile-detail-card';
import OfficeCard from './cards/office-card';
import stylesT from './styles';
import EditPartnerPRL from './editors/edit-partner-prl';
import BillingInfoCard from './cards/billing-info-card';

const profileDetails = [
  {
    attributeName: 'aboutUs',
    displayName: 'About Us',
  },
  {
    attributeName: 'cancellationPolicy',
    displayName: 'Cancelation Policy',
  },
  {
    attributeName: 'paymentOptions',
    displayName: 'Payment Options',
  },
  {
    attributeName: 'paymentTerms',
    displayName: 'Payment Terms',
  },
];

const View = ({
  contextMenuAnchors,
  contextMenusOpen,
  editorsOpen,
  onCloseContextMenu,
  onCloseEditor,
  onEditAvatar, // eslint-disable-line @typescript-eslint/no-unused-vars
  onOpenContextMenu,
  onOpenEditor,
  partner,
  prlValue,
  onUpdatePRLValue,
}: ViewProps) => {
  const theme = useTheme();
  const styles = stylesT(theme);
  const [openEditPRL, setOpenEditPRL] = useState(false);

  const resetPRL = () => {
    setOpenEditPRL(false);
    onCloseContextMenu('partnerReferralProgram');
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{
        py: {
          desktop: 6.5,
          mobile: 3.5,
        },
      }}
      >
        <Grid container spacing={2.625} sx={styles.gridWrapper}>
          <Grid item sx={{ flex: 2 }}>
            <Stack spacing={2.5}>
              <Box>
                <ProfileCard
                  contextMenuAnchor={contextMenuAnchors.default}
                  contextMenuOpen={!!contextMenusOpen.default}
                  onCloseContextMenu={() => onCloseContextMenu('default')}
                  onEditAvatar={onEditAvatar}
                  onOpenContextMenu={(e) => onOpenContextMenu('default', e)}
                  onOpenEditor={() => onOpenEditor('default')}
                  partner={partner}
                />
                <PartnerEditor
                  open={!!editorsOpen.default}
                  onClose={() => onCloseEditor('default')}
                />
              </Box>
              <Divider sx={styles.dividerDesktop} />
              <Box>
                <ProfileDetailCard
                  contextMenuAnchor={contextMenuAnchors.partnerReferralProgram}
                  contextMenuOpen={!!contextMenusOpen.partnerReferralProgram}
                  onCloseContextMenu={() => onCloseContextMenu('partnerReferralProgram')}
                  onOpenContextMenu={(e) => onOpenContextMenu('partnerReferralProgram', e)}
                  onOpenEditor={() => setOpenEditPRL(true)}
                  partner={{ ...partner!, partnerReferralProgram: prlValue || false }}
                  partnerDetail={{ attributeName: 'partnerReferralProgram', displayName: 'PRL Program Opt-In' }}
                />
                <EditPartnerPRL
                  partner={partner!}
                  value={prlValue || false}
                  open={openEditPRL}
                  handleClose={resetPRL}
                  handleSubmit={(newValue) => { onUpdatePRLValue(newValue); resetPRL(); }}
                />
              </Box>
              <Box>
                <BillingInfoCard
                  billingInfo={partner?.billingAddress}
                  contextMenuAnchor={contextMenuAnchors.billingInfo}
                  contextMenuOpen={!!contextMenusOpen.billingInfo}
                  onCloseContextMenu={() => onCloseContextMenu('billingInfo')}
                  onOpenContextMenu={(e) => onOpenContextMenu('billingInfo', e)}
                  onOpenEditor={() => onOpenEditor('billingInfo')}
                />
                <BillingInfoEditor
                  open={!!editorsOpen.billingInfo}
                  onClose={() => onCloseEditor('billingInfo')}
                />
              </Box>
              { partner?.offices?.map((office, i) => (
                <Box key={office.city ? `${office.city}-${i}` : `default-city-${i}`}>
                  <OfficeCard office={office} />
                </Box>
              ))}
              <Divider sx={styles.dividerMobile} />
            </Stack>
          </Grid>
          <Grid item sx={{ flex: 3 }}>
            <Stack spacing={2.5}>
              { profileDetails.map((partnerDetail, i) => (
                <React.Fragment key={partnerDetail.attributeName}>
                  <Box key={partnerDetail.attributeName}>
                    <ProfileDetailCard
                      contextMenuAnchor={contextMenuAnchors[partnerDetail.attributeName]}
                      contextMenuOpen={!!contextMenusOpen[partnerDetail.attributeName]}
                      onCloseContextMenu={() => onCloseContextMenu(partnerDetail.attributeName)}
                      onOpenContextMenu={(e) => onOpenContextMenu(partnerDetail.attributeName, e)}
                      onOpenEditor={() => onOpenEditor(partnerDetail.attributeName)}
                      partner={partner}
                      partnerDetail={partnerDetail}
                    />
                    <PartnerDetailEditor
                      open={!!editorsOpen[partnerDetail.attributeName]}
                      onClose={() => onCloseEditor(partnerDetail.attributeName)}
                      partnerDetail={partnerDetail}
                    />
                  </Box>
                  { i === 0 && <Divider sx={styles.dividerMobile} /> }
                </React.Fragment>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default View;
